import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";

import fpItemCycleTimeService from "@plannel/services/fp/fp-item-cycle-time-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import FpGridSetting, {
  readHide, createSumAwareFormatter, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val 이식 — 버전 선택 시 종료일 = 시작일 + N일(§A-3c).
// old ItemCycleTime.jsx는 `let fpSettings = { searchZone: 14 }`를 코드 기본값으로 두고 loadSettings()로
// DB 값을 덮어썼는데, 그 DB 값이 화면/planScope 무관 단일 고정값(9)임이 §A-3c에서 확인됐다 —
// 즉 14는 DB 응답 도착 전 한 순간만 쓰이던 폴백이라 이식 대상이 아니고, 다른 Report 화면
// (PlanResult/ResourceUtilization/MaterialPsi 등)과 동일하게 9를 화면 상수로 고정한다.
// TODO(MIG-T2): [itemcycletime/ItemCycleTime] 조회조건 세팅(버킷 목록/검색구간)의 테넌트별 커스터마이징 미이관.
//   old: `search-option/bucket`(버킷 목록+기본값), `setting` `FP_SRH_RANGE.attr01Val`(기본 조회구간 일수)을
//        각각 DB에서 조회(`periodSearchGroupRef.setBucket(planScope, viewId)` / `loadSettings()`)
//   사유: 정적 구성(JSON 카탈로그 / 화면 상수)으로 전환 — 가이드 §A-3~§A-3d에서 Report 공통으로 결정
const SEARCH_ZONE_DAYS = 9;

const PARAM_DATE_FORMAT = "yyyyMMdd";

// 백엔드가 부분합 행에 넣는 표식 — 그리드에선 번역해 보여주고, 차트 대상에서는 제외한다.
// old styleCallback은 customerCd/itemGrpCd/itemCd 중 하나가 'SUM'이면 summary-col을 붙였다.
// (현재 `AnalysisController#getItemCycleTime`은 SUM 행을 만들지 않지만, old가 방어적으로 걸러내던
//  코드라 그대로 유지한다 — 백엔드가 나중에 부분합 행을 붙여도 화면이 깨지지 않는다.)
const SUM_ROW = "SUM";

// 드릴다운 필터 순서 — 차트 재조회 파라미터 구성과 "이전 클릭과 같은가" 비교에 쓴다
// (old updateChartFromGrid의 `column.startsWith('customer'|'itemGrp')` 3단 분기와 동일).
// 백엔드가 이 3개 값으로 GroupingLevel(CUSTOMER / CUSTOMER_ITEM_GRP / CUSTOMER_ITEM_GRP_ITEM)을
// 정해 그 레벨까지 집계한 1행만 내려주므로(ItemCycleTimeCrosstabDto.merge), 차트는 항상 단일 대상이다.
const HIERARCHY_FIELDS = ["customerCd", "itemGrpCd", "itemCd"];

// 트리(행 그룹) 계층 — 고객코드 > 고객명 > 품목그룹코드 > 품목그룹명 > (leaf)품목.
// old 컬럼정의의 customerCd/customerNm/itemGrpCd/itemGrpNm/itemCd/itemNm 전부 `mergeRule`
// (`criteria: "prevvalues + value"`)이라 Tree Data가 아니라 Row Grouping을 고른다(§A-6b 판단표).
const GROUP_FIELDS = ["customerCd", "customerNm", "itemGrpCd", "itemGrpNm"];

const CUSTOMER_CD_COL_ID = "customerCdGrp";
const CUSTOMER_NM_COL_ID = `CUSTOMER_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_GRP_CD_COL_ID = "itemGrpCdGrp";
const ITEM_GRP_NM_COL_ID = `ITEM_GRP_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const CUSTOMER_NM_HIDE_KEY = "customerNmColsHide";
const ITEM_GRP_NM_HIDE_KEY = "itemGrpNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";

// old ItemCycleTimeChart의 2계열 색 — `themeStore.t3smartscm.fpChartPriority.color2`(막대, 싸이클타임) /
// `.color1`(선, 전체 평균). 테마 값 자체는 런타임 스토어라 소스에 없지만, 같은 테마 키를 쓰는
// CYCLE_TIME_COLOR는 old InoutStatus.jsx(color2=FP_PRODUCTION 막대)를 이미 이관한
// `report/InoutStatus.js`가 확정해둔 hex와 동일하게 맞춘다(색상 매핑 선례 재사용). TOTAL_AVG_COLOR는
// 원래 InoutStatus.js(color1)와 동일한 "#222E3C"를 썼으나, 거의 검정으로 보여 다른 FP 리포트
// 화면들(MaterialPsi/PsiDetail/ProdSalesStockStatus)의 재고 트렌드 라인 색(#4472C4, 중간 톤 블루)로
// 통일한다(2026-08-11 사용자 지시).
const CYCLE_TIME_COLOR = "#6DE36F";
const TOTAL_AVG_COLOR = "#4472C4";

const ItemCycleTime = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  // 응답 순서 뒤바뀜 방지 — 조회(그리드)와 차트는 서로 다른 요청이라 가드도 2개(§0-8).
  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  // old prevChartParamsRef — 같은 (customer, itemGrp, item) 조합이면 차트를 다시 조회하지 않는다.
  const prevChartParamsRef = useRef(null);
  // 조회 성공 직후 첫 행에 focus를 걸 때 쓴다 — setRowData는 React state 갱신이라 그 자리에서 곧바로
  // api.setFocusedCell을 부르면 그리드가 새 rowData를 아직 반영하기 전일 수 있다(레이스, §A-7).
  const pendingFocusRef = useRef(null);
  // columnDefs를 조회 응답 시점에 다시 만들어 넘기므로, 최신 조회조건을 콜백에서 읽기 위한 ref.
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  // planScope→plan-dts→planningDate→versions→versionCd/plantCd→기간 재계산 캐스케이드가 여러 렌더에
  // 걸쳐 순차 커밋되면서 각 단계가 독립적으로 자동조회를 트리거하는 것을 하나로 코앨레싱한다(§A-9b).
  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveItemCycleTimes(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  // 화면을 떠날 때 하단 상태바의 선택 브레드크럼(전역 스토어)을 비운다(§A-7).
  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      // 조회 전용 화면 — 공용 DefaultGridSetting의 defaultColDef.editable:true를 끈다(§A-9).
      // old도 setNoneEditableGrid(itemCycleTimeGrid)로 전체를 비편집으로 잠갔다.
      defaultColDef: { ...base.defaultColDef, editable: false },
      // old styleCallback의 'summary-col' 강조 대응 — customerCd/itemGrpCd/itemCd 중 하나가 SUM인 행.
      getRowStyle: (params) => {
        const row = params.data;
        if (!row) return null;
        const isSum = row.customerCd === SUM_ROW || row.itemGrpCd === SUM_ROW || row.itemCd === SUM_ROW;
        return isSum ? { backgroundColor: grey[100] } : null;
      },
      // 행 그룹(트리) 3종 세트 — §A-6b / ResourceUtilization.js와 동일 조합.
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old displayCallback(`value === 'SUM' ? transLangKey(value) : value`) 대응. old는 itemGrp*/item*
  // 4개 컬럼에만 걸었지만, 고객 레벨 부분합이 생기면 코드가 그대로 노출되므로 PlanResult.js/
  // CustomerLeadTime.js와 동일하게 코드/명칭 컬럼 전체에 같은 포매터를 적용한다.
  const sumAwareFormatter = createSumAwareFormatter(SUM_ROW, t);

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns/gridItems의 7개(customerCd/customerNm/itemGrpCd/
  // itemGrpNm/itemCd/itemNm/totalAvgCycleTime)와 1:1이다. old엔 PlanResult/CustomerLeadTime의
  // displayColor류 숨김 컬럼이 없다(차트 색이 테마 상수라 행 데이터에 색 필드가 없음) — 그래서
  // 여기에도 숨김 색상 컬럼을 만들지 않는다.
  //  - 고객/품목그룹은 행 그룹(트리)로 접고 펼친다(§A-6b): 데이터 컬럼은 rowGroup+hide, 화면엔
  //    showRowGroup 표시 컬럼만.
  //  - 품목은 트리의 leaf라 평범한 field 컬럼.
  const fixedColumnDefs = useMemo(() => [
    // 1) 실제 그룹 기준이 되는 데이터 컬럼 — 화면엔 안 보이고 계층만 만든다(배열 순서 = 그룹 depth).
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    // 2) 화면에 보이는 그룹 표시 컬럼(코드 + 화살표로 열리는 명칭)
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, CUSTOMER_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: CUSTOMER_CD_COL_ID, cdField: "customerCd", cdHeader: "FP_CUSTOMER_CD",
      nmColId: CUSTOMER_NM_COL_ID, nmField: "customerNm", nmHeader: "FP_CUSTOMER_NM",
      hideKey: CUSTOMER_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_GRP_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: ITEM_GRP_CD_COL_ID, cdField: "itemGrpCd", cdHeader: "FP_ITEM_GRP_CD",
      nmColId: ITEM_GRP_NM_COL_ID, nmField: "itemGrpNm", nmHeader: "FP_ITEM_GRP_NM",
      hideKey: ITEM_GRP_NM_HIDE_KEY, width: 150
    }),

    // 3) leaf(품목) — 그룹이 아니라 실제 행의 값. 코드/명칭 토글은 동일하게 적용.
    {
      headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 150, pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), reduxKey: ITEM_NM_HIDE_KEY, detailColumns: [ITEM_NM_COL_ID]
      }
    },
    {
      headerName: "FP_ITEM_NM", field: "itemNm", colId: ITEM_NM_COL_ID, width: 150, pinned: "left", lockPinned: true,
      hide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter
    },

    // old headerText는 리터럴 "AVG"(전체 평균 싸이클타임) — new엔 그 키가 없어서(lang-pack-fp.tsv에도
    // 없음) 같은 값을 의미하는 기존 키 FP_TOTAL_AVG_CYCLE_TIME(§0-3 "죽은 키 만들지 말 것")을 그대로
    // 재사용한다(차트의 기준선 계열 라벨과도 같은 값이라 일관적이다). 값이 "일(day)" 단위 평균이라
    // 정수가 아니라 소수다 — 공용 numberColumn은 소수 0자리로 잘라내(maximumFractionDigits:0) 1일
    // 미만 값이 전부 0으로 보이므로 decimalPointColumn(최대 2자리)을 쓴다.
    { headerName: "FP_TOTAL_AVG_CYCLE_TIME", field: "totalAvgCycleTime", colId: "totalAvgCycleTime", width: 130,
      pinned: "left", lockPinned: true, type: ["decimalPointColumn", "rightAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // crosstabHeader("2026-07-20_W30" / 일버킷이면 "2026-07-20_MON")를 "_" 앞(날짜)=그룹,
  // 뒤(주차/월/요일)=자식으로 묶는다(§A-6). 요일 코드 번역+토/일 강조색은 common/grid.js의
  // buildDayBucketPivotColumnDefs가 처리한다(old setColumnColorOnSunSat 대응, §A-6d/§A-6f).
  // 값이 "일" 단위 평균 싸이클타임(소수)이라 기본 numberColumn이 아니라 decimalPointColumn이
  // 필요해 childExtraProps로 type을 덮어쓴다(위 AVG 컬럼과 같은 이유).
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t, {
    childExtraProps: () => ({ type: ["decimalPointColumn", "rightAligned"] })
  });

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      customerCd: row.customerCd, customerNm: row.customerNm,
      itemGrpCd: row.itemGrpCd, itemGrpNm: row.itemGrpNm,
      itemCd: row.itemCd, itemNm: row.itemNm,
      totalAvgCycleTime: row.totalAvgCycleTime
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  // 조회/차트 공통 파라미터(old searchParams의 공통부). 캐스케이드 중 여러 번 트리거돼도 디바운스가
  // 실제 호출을 마지막 트리거 이후로 늦춰주므로 실행 시점엔 searchStateRef가 최신값이다.
  // useGrouping은 그리드 조회에서 old와 동일하게 false를 명시 전송한다 — 백엔드
  // GroupingLevel.get()이 false면 CUSTOMER_ITEM_GRP_ITEM(품목까지 전부 펼친 그리드용)으로 집계한다.
  // old가 보내던 localeDateFormat은 보내지 않는다 — 백엔드 Bucket이 자체 기본 포맷을 쓰고, 이미
  // 이관된 다른 Report 화면(PlanResult 등)도 전부 보내지 않는 쪽으로 통일돼 있다.
  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod,
      useGrouping: false
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartSeries([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 왼쪽의 "지금 보고 있는 위치" 브레드크럼(공용 FilterStatusPanel) 갱신 —
  // old의 차트 위 타이틀(getChartTitle)을 이 패널로 완전히 대체한다(§A-7 "차트 타이틀 — 쓰지 않는다").
  // FilterStatusPanel은 미리 정해둔 슬롯을 originLoc→destLoc→customer→item→workCenter→resource
  // 순서로 렌더한다. 이 화면의 드릴다운 3단(고객 > 품목그룹 > 품목)을 그 순서대로 보이게 하려면
  // 렌더 순서가 1·2·4번인 슬롯에 실어야 하므로 고객→originLoc, 품목그룹→destLoc, 품목→item에
  // 매핑한다(PlanResult.js가 공장→originLoc/자원→destLoc/품목→item으로 쓴 것과 같은 원리 —
  // 라벨 문구는 *Header 값으로 직접 넣으므로 슬롯 이름의 원래 도메인 의미와 무관하게 정확히 보인다).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.customerCd ?? null, originLocCdHeader: t("FP_CUSTOMER_CD"),
      originLocNm: target.customerNm ?? null, originLocNmHeader: t("FP_CUSTOMER_NM"),
      destLocCd: target.itemGrpCd ?? null, destLocCdHeader: t("FP_ITEM_GRP_CD"),
      destLocNm: target.itemGrpNm ?? null, destLocNmHeader: t("FP_ITEM_GRP_NM"),
      itemCd: target.itemCd ?? null, itemCdHeader: t("FP_ITEM_CD"),
      itemNm: target.itemNm ?? null, itemNmHeader: t("FP_ITEM_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. target = { customerCd, customerNm, itemGrpCd, ... }
  // (상위 레벨을 클릭하면 하위 코드는 없음 — old updateChartFromGrid와 동일).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;
    // SUM(부분합) 셀은 차트 갱신 대상에서 제외(old `getValue(itemIndex, column) === 'SUM'` 분기).
    if (target.customerCd === SUM_ROW || target.itemGrpCd === SUM_ROW || target.itemCd === SUM_ROW) return;

    updateStatusBreadcrumb(target);

    const nextParams = {};
    HIERARCHY_FIELDS.forEach(field => { nextParams[field] = target[field] ?? ""; });
    const prev = prevChartParamsRef.current;
    if (prev && HIERARCHY_FIELDS.every(field => prev[field] === nextParams[field])) return;
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    // old loadChartData는 useGrouping:'true'를 명시 전송한다 — 클릭한 레벨까지만 집계한 1행을 받는다.
    const params = { ...buildBaseParams(), plantCds: version.plantCd, ...nextParams, useGrouping: "true" };

    // TODO(MIG-T2): [itemcycletime/ItemCycleTime] 그리드 컬럼 필터(값 목록 체크박스) + 그 선택값의 차트 조회 연동 미이관.
    //   old: customerCd/itemGrpCd/itemCd에 RealGrid autoFilter, 필터 변경(onFilteringChanged)이
    //        현재 선택 행 기준으로 차트를 다시 그림(clearAllFilters로 조회마다 초기화)
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다(§A-6e — PlanResult/CustomerLeadTime과 동일 사유).
    //        게다가 new `AnalysisController#getItemCycleTime`은 old와 달리 changes(FilterParameter)
    //        자체를 읽지 않으므로, 2차엔 프론트 트리거 UI뿐 아니라 백엔드 지원도 함께 필요하다.
    fpItemCycleTimeService.getItemCycleTimes(params)
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = (data ?? []).filter(r => r.itemCd !== SUM_ROW && r.customerCd !== SUM_ROW);

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartSeries([]);
          setChartLabels([]);
          return;
        }

        // old는 응답 행마다 (막대=pivotData, 선=totalAvgCycleTime 상수배열) 2개 데이터셋을 만들었고,
        // useGrouping:'true'로 백엔드가 클릭 레벨까지 집계해 항상 1행만 내려주므로 실질적으로
        // "막대 1계열 + 선 1계열"이다(ItemCycleTimeCrosstabDto.merge / GroupingLevel 확인).
        // 방어적으로 여러 행이 와도 계열 수를 늘리지 않고 버킷별 평균을 낸다 — 싸이클타임은
        // 평균 지표라 합산이 의미가 없다.
        const bucketSum = chartHeader.map(() => 0);
        const bucketCount = chartHeader.map(() => 0);
        let totalAvgSum = 0;

        for (const row of rows) {
          chartHeader.forEach((_, index) => {
            const value = row.pivotData?.[index];
            if (value === null || value === undefined) return;
            bucketSum[index] += value;
            bucketCount[index] += 1;
          });
          totalAvgSum += row.totalAvgCycleTime ?? 0;
        }

        const cycleTimeLabel = t("FP_CYCLE_TIME");
        const totalAvgLabel = t("FP_TOTAL_AVG_CYCLE_TIME");
        const totalAvg = totalAvgSum / rows.length;

        // PivotChartComposed는 롱 포맷 입력이라 groupKey 필드의 런타임 값이 곧 범례(계열)가 된다 —
        // old의 고정 2계열을 "계열 이름을 값으로 갖는 2행"으로 표현한다(중복 없는 groupKey, §A-9).
        const cycleTimePoint = { series: cycleTimeLabel };
        const totalAvgPoint = { series: totalAvgLabel };
        chartHeader.forEach((header, index) => {
          cycleTimePoint[header] = bucketCount[index] > 0 ? bucketSum[index] / bucketCount[index] : 0;
          // old lineArray = Array(pivotData.length).fill(totalAvgCycleTime) — 전 구간 수평 기준선.
          totalAvgPoint[header] = totalAvg;
        });

        setChartLabels(chartHeader);
        setChartData([cycleTimePoint, totalAvgPoint]);
        // y축을 하나로 합치려면 모든 계열이 같은 groupName을 가져야 한다(§A-9).
        // 라벨은 old ItemCycleTimeChart의 `(${transLangKey('Days')})` 이식. new엔 "DAYS"(대문자) 키가
        // 없고, 날짜 선택기 등에서 이미 쓰이는 소문자 "days"("Days") 키가 의미상 동일해 재사용한다
        // (§0-3 "죽은 키 만들지 말 것" — 새로 짓기 전에 재사용 가능한 키부터 확인).
        const yAxisLabel = `(${t("days")})`;
        setChartSeries([
          {
            field: cycleTimeLabel, text: cycleTimeLabel, type: "bar", groupName: yAxisLabel,
            typeOptions: { stackId: "itemCycleTime", fill: CYCLE_TIME_COLOR }
          },
          {
            // fill을 같이 넘기면 PivotChartComposed(공용)가 line 렌더링 시 stroke를 fill 값으로
            // 덮어써 선이 투명해지는 부작용이 있다(점만 보이고 선이 안 보이는 원인) — fill 없이
            // stroke만 넘긴다.
            field: totalAvgLabel, text: totalAvgLabel, type: "line", groupName: yAxisLabel,
            typeOptions: { stroke: TOTAL_AVG_COLOR }
          }
        ]);
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveItemCycleTimes = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    // old의 `!isEntirePlanPeriod && !startDate || !endDate`는 연산자 우선순위 버그로 사실상 endDate만
    // 검사한다 — 의도대로 "구간 전체가 아니면 시작·종료 둘 다 필수"로 구현한다(§A-9).
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd };

    // 조회 중 AG Grid 내장 로딩 오버레이 표시(§0-8b 공통 컨벤션).
    gridRef.current?.api?.setGridOption("loading", true);

    fpItemCycleTimeService.getItemCycleTimes(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        // 조회할 때마다 버킷이 달라져 컬럼 구성 자체가 바뀐다 → columnDefs를 다시 만들어 재정의한다.
        // p13n(컬럼상태 저장)은 이 그리드에 의미가 없어 initState:false(§A-6c).
        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행 기준으로 차트를 그리고 그리드의 현재 선택 셀도 그 행으로 옮긴다
        // (old `gridView.setFocus()` + `setCurrent({itemIndex:0, column:'itemCd'})`). 트리에서
        // rowIndex 0은 첫 고객 그룹 행이고, old도 첫 행의 (customerCd, itemGrpCd, itemCd) 전체를
        // 그대로 setChart에 넘겼으므로 품목 레벨 차트로 시작한다 — 선택 셀은 품목 표시 컬럼에 맞춘다.
        pendingFocusRef.current = { rowIndex: 0, colId: "itemCd" };
        prevChartParamsRef.current = null;
        loadChart({
          customerCd: rows[0].customerCd, customerNm: rows[0].customerNm,
          itemGrpCd: rows[0].itemGrpCd, itemGrpNm: rows[0].itemGrpNm,
          itemCd: rows[0].itemCd, itemNm: rows[0].itemNm
        });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  // rowData가 실제로 그리드에 반영된 시점 — pendingFocusRef에 예약해둔 포커스를 여기서 소비한다.
  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  // 클릭 컬럼 → 드릴다운 레벨. old updateChartFromGrid의 `column.startsWith('customer'|'itemGrp')`
  // 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것.
  const CUSTOMER_LEVEL_COL_IDS = new Set([CUSTOMER_CD_COL_ID, CUSTOMER_NM_COL_ID]);
  const ITEM_GRP_LEVEL_COL_IDS = new Set([ITEM_GRP_CD_COL_ID, ITEM_GRP_NM_COL_ID]);

  // groupHideOpenParents 때문에 두 경우를 모두 다뤄야 한다(§A-6b):
  //  - 그룹 행(접힌 그룹의 대표 행): 조상 체인의 (field, key)를 모아 그 행이 대표하는 범위를 만든다.
  //  - leaf(품목) 행: 그룹이 펼쳐져 있으면 고객/품목그룹 값이 이 행에 렌더되므로 old처럼 클릭한
  //    컬럼으로 레벨을 정한다.
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (CUSTOMER_LEVEL_COL_IDS.has(colId)) {
      return { customerCd: row.customerCd, customerNm: row.customerNm };
    }
    if (ITEM_GRP_LEVEL_COL_IDS.has(colId)) {
      return {
        customerCd: row.customerCd, customerNm: row.customerNm,
        itemGrpCd: row.itemGrpCd, itemGrpNm: row.itemGrpNm
      };
    }
    return row;
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  // 하단 상태바 3종(브레드크럼 / 전체 행 수 / 셀 범위 집계) — Report 전 화면 공통 구성(§A-7).
  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod). 그룹을 넘나드는 로직이라
  // PeriodSearchGroup이 아니라 화면이 소유한다(§A-1b).
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      // 버전이 없어지면 조회 구간도 이전 값을 들고 있지 않고 같이 비운다(old setVersionPeriod('clear')).
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  // PlanScope를 바꿔 하위(버전/기간)까지 빈 값으로 리셋된 경우, 그리드·차트에 이전 계획범위의 결과가
  // 남아 있으면 안 된다 — versionCd가 비면 같이 비운다(§A-1b).
  useEffect(() => {
    if (versionState.versionCd) return;

    // 리셋 직전에 예약돼 있을 수 있는 디바운스 호출을 취소한다(§A-9b).
    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 화면 전체 조회 버튼을 없앤 대신, 선택형 조건(버전/plant/기간/버킷)이 바뀌면 값이 준비되는 즉시
  // 자동 조회한다(§A-9b). versionCd 변경은 위 effect가 periodState까지 갱신하며 같은 디바운스를
  // 호출하므로 여기 의존성에 직접 넣지 않는다.
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
  // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
  const pivotConfig = useMemo(() => ({
    groupKey: "series",
    chartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartSeries, chartLabels]);

  return (
    <>
      {/* old SearchRow = [VersionSearchGroup, PeriodSearchGroup, SearchOptsGroup]. 이 화면은
          `searchOptGroupRef.current.setSrhOpts(...)`를 어디서도 호출하지 않아 SearchOptsGroup이
          렌더할 필드를 하나도 받지 못하고(내부 srhOptsJSX가 null 그대로), loadData/loadChartData도
          그 값을 읽지 않는다 — old에서도 실제로 아무 입력칸이 없던 자리라 이관 대상이 아니다
          (PlanResult/InoutStatus처럼 필드가 실제로 쓰이는 화면과 다른 점, old 소스 직접 확인).
          그래서 SearchOptsGroupRetrieve를 두지 않는다.
          엑셀 버튼은 FilterContainer의 마지막 자식 자리(접기 토글 왼쪽)에 온다 — hasIcons는 생략(§0-8). */}
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // Report 화면 bucketCase 배정표(§A-3) — ResProdPlan/ResrcUtilization 2개 화면만
          // WITHOUT_HOUR이고 이 화면(UI_FP_ITEM_CYCLE_TIME)은 예외 목록에 없어 WITH_HOUR가 기본.
          bucketCase={BUCKET_CASE.WITHOUT_HOUR}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old VerticalGridChartBox(그리드 위/차트 아래, 명시적 비율 없이 1:1에 가까움) 대응 —
          SplitterContainer 기본값([3,2]=60/40)을 그대로 두면 1:1이 아니게 되므로 명시한다(§A-1c). */}
      <SplitterContainer initialSizes={[1, 1]}>
        {/* split bar에 붙는 쪽에 8px 여백(§A-1c) — height:100%에 padding을 주면 pane 높이를 넘친다. */}
        <Box id="fp-item-cycle-time-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* old ItemCycleTimeChart.jsx는 `plugins={[ChartLegendPlugin, ChartAxisTitlePlugin]}`로
              실제로 범례를 그렸다(CustomerLeadTime과 같은 케이스, PlanResult와는 다름 — §A-7의
              "plugins 배열을 직접 확인" 경고대로 확인함) → recharts 기본 Legend를 숨기지 않는다.
              TODO(MIG-T2): [itemcycletime/ItemCycleTime] 차트 범례 클릭 토글(시리즈 숨김+취소선) 미이관.
                old: ChartLegendPlugin — 범례 항목 클릭 시 해당 시리즈 숨김 + 취소선 스타일
                사유: recharts 기본 <Legend/>는 클릭 토글이 없고 PivotChartComposed에 대응 훅도 없다 —
                     §A-7에서 Report 카테고리 공통 Tier2로 이미 확정된 항목. 범례 표시 자체·막대/선
                     2계열·툴팁·드릴다운 등 핵심 시각화는 정상 이관됨. old가 plugins에 포함하지 않은
                     ChartHatchPatternPlugin/ChartDataLabelPlugin/hover dim은 애초에 대상이 아니다. */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            {/* decimalPoint:true — PivotChartComposed는 기본적으로 값을 Math.round()한다
                (PivotChartComposed.js:245). 싸이클타임은 "일" 단위 소수라 반올림하면 1일 미만 값이
                전부 0이 되어 차트가 비어 보인다. minHeight:"0px"는 스플리터 고정 영역에서 기본값
                (240px)이 넘쳐 안 보이는 스크롤을 만드는 문제 회피(§A-9의 PlanResult 선례). */}
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ItemCycleTime);
